/**
 * Breadcrumbs — reads handle.crumb from every matched route via useMatches().
 *
 * Rules:
 *   - Only renders when there are 2+ crumb segments (hides on /dashboard).
 *   - crumb can be a plain string OR a function (params) => string.
 *   - The last crumb is aria-current="page" and non-linked.
 *   - Separators are a presentational "→" span (aria-hidden).
 *
 * Mount this component above <Outlet /> in AppLayout.
 */

import { Link, useMatches } from 'react-router-dom';

import { cn } from '@/lib/utils';

// Shape of the handle we read. A crumb can be a static string
// or a function that receives the route params and returns a string.
type CrumbValue = string | ((ctx: { params: Record<string, string | undefined> }) => string);

interface RouteHandle {
  crumb?: CrumbValue;
  title?: string;
}

interface MatchWithHandle {
  id: string;
  pathname: string;
  params: Record<string, string | undefined>;
  handle: unknown;
}

function resolveCrumb(
  crumb: CrumbValue,
  params: Record<string, string | undefined>
): string {
  if (typeof crumb === 'function') {
    return crumb({ params });
  }
  return crumb;
}

export default function Breadcrumbs() {
  const matches = useMatches() as MatchWithHandle[];

  // Collect only matches that carry a crumb on their handle
  const crumbs = matches
    .filter(
      (m): m is MatchWithHandle & { handle: RouteHandle & { crumb: CrumbValue } } =>
        typeof (m.handle as RouteHandle | null)?.crumb !== 'undefined'
    )
    .map((m) => ({
      label: resolveCrumb(m.handle.crumb, m.params),
      to: m.pathname,
    }));

  // Hide when there is only one segment (e.g. /dashboard alone)
  if (crumbs.length < 2) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 px-8 py-3 border-b border-border bg-background text-sm"
    >
      <ol className="flex items-center gap-1.5 list-none m-0 p-0">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;

          return (
            <li key={crumb.to} className="flex items-center gap-1.5">
              {/* Separator — hidden from screen readers */}
              {idx > 0 && (
                <span aria-hidden="true" className="text-muted-foreground select-none">
                  →
                </span>
              )}

              {isLast ? (
                // Current page — no link, has aria-current
                <span
                  aria-current="page"
                  className={cn('text-foreground font-medium truncate max-w-[200px]')}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className={cn(
                    'text-muted-foreground hover:text-foreground transition-colors',
                    'underline-offset-2 hover:underline',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'focus-visible:ring-offset-2 rounded-sm'
                  )}
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
